import { Info } from 'lucide-react'
import type { ReactNode } from 'react'

interface InfoHintProps {
  /** The formal one-liner that opens the hint. */
  title: string
  /** The plain-language explanation underneath it. */
  children: ReactNode
  /** Accessible name for the button, for screen readers. */
  label: string
}

/**
 * The small “i” beside a heading (GM request, 2026-10-17).
 *
 * The rule it explains is still enforced by the server whichever way the UI is read, so
 * the text lives behind a hint instead of shouting across the page. Opens on hover and
 * on keyboard focus — `group-focus-within` is what makes it reachable without a mouse.
 */
export function InfoHint({ title, children, label }: InfoHintProps) {
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label={label}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 text-slate-400 transition-colors hover:border-salsa-400 hover:text-salsa-500 focus-visible:border-salsa-400 focus-visible:text-salsa-500 dark:border-white/15 dark:text-slate-500 dark:hover:border-salsa-500/40 dark:hover:text-salsa-400"
      >
        <Info className="h-2.5 w-2.5" aria-hidden="true" />
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-50 mt-2 hidden w-80 max-w-[80vw] rounded-xl border border-slate-200 bg-white p-3 text-left shadow-xl group-hover:block group-focus-within:block dark:border-white/10 dark:bg-ink-900"
      >
        <span className="block text-[11px] font-semibold text-slate-800 dark:text-slate-100">
          {title}
        </span>
        <span className="mt-1 block text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
          {children}
        </span>
      </span>
    </span>
  )
}
